import { Auth, CognitoUser } from "@aws-amplify/auth";
import { Hub } from "@aws-amplify/core";
import { CognitoUserSession } from "amazon-cognito-identity-js";
import axios from "axios";
import { TOKEN_STORAGE_KEY, ASSETS_LAST_MODIFIED_KEY, SIMULATE_LDAP_GROUPS_STORAGE_KEY } from "../types/constants";
import { parseBadgeSiteCode } from "../utils/siteCode";

/**
 * Interface for user data structure
 */
export interface UserData {
  alias: string;
  token: string;
  lastName: string;
  firstName: string;
  email: string;
  ldap: string[];
  siteCode: string;
}

/**
 * Configure AWS Amplify
 * This should be called during app initialization
 */
export const configureAmplify = () => {
  Auth.configure({
    region: "us-east-1",
    userPoolId: "us-east-1_MOeCfPHyQ",
    userPoolWebClientId: "63q5d3tditi853nnpuu9be2ukb",
    oauth: {
      domain: "whs-navigator-beta-na.auth.us-east-1.amazoncognito.com",
      scope: ["openid", "email", "profile"],
      redirectSignIn: "https://beta.na.navigator.whs.amazon.dev",
      redirectSignOut: "https://beta.na.navigator.whs.amazon.dev",
      responseType: "code",
    },
  });
};

/**
 * Get the currently authenticated user
 */
const getCurrentUser = async (): Promise<CognitoUser | null> => {
  try {
    return await Auth.currentAuthenticatedUser();
  } catch (e) {
    console.error("Error getting current user:", e);
    return null;
  }
};

/**
 * Initiate federated sign-in process
 */
const signIn = () =>
  Auth.federatedSignIn({
    customProvider: "FederateOIDC",
    customState: window.location.href,
  });

/**
 * Handle login process
 * - Check for session
 * - Check for assets changes
 * - Set up listener for OAuth state
 * - Extract user info from token
 */
const login = async (): Promise<UserData> => {
  const stage: string = "beta";
  
  // Check for assets changes to ensure latest version is used
  if (stage !== "test") {
    const lastModified = await getAssetsLastModified();
    if (localStorage.getItem(ASSETS_LAST_MODIFIED_KEY) === null) {
      localStorage.setItem(ASSETS_LAST_MODIFIED_KEY, lastModified);
    } else if (localStorage.getItem(ASSETS_LAST_MODIFIED_KEY) !== lastModified) {
      localStorage.removeItem(ASSETS_LAST_MODIFIED_KEY);
      // Refresh page to get latest assets. The reload only starts a navigation,
      // and this page keeps running until the browser replaces it. Never settle,
      // so the app stays on its loading screen instead of rendering and sending
      // requests the browser cancels.
      window.location.reload();
      return new Promise<UserData>(() => {});
    }
  }

  // Initialize empty user data structure
  const currentUser: UserData = {
    alias: "",
    token: "",
    lastName: "",
    firstName: "",
    email: "",
    ldap: [],
    siteCode: "",
  };

  // Listen for the customState event from federated sign in
  Hub.listen("auth", ({ payload: { data, event } }) => {
    switch (event) {
      case "customOAuthState":
        // set current url to federatedSignIn customState
        window.location.href = data;
        break;
    }
  });

  // Get current authenticated user
  const user = await getCurrentUser();

  // If no user, start sign in flow
  if (!user) {
    console.info("No session found, attempting to start a new one...");
    await signIn();
  }

  // Extract user information from session
  if (user) {
    user.getSession((_: Error | null, session: CognitoUserSession) => {
      if (session) {
        // Get token and decode payload
        const token = session.getIdToken().getJwtToken();
        currentUser.token = token;
        
        // Store token in localStorage for API requests
        localStorage.setItem(TOKEN_STORAGE_KEY, token);
        
        // Decode JWT token to get user info
        const decodeToken = session.getIdToken().decodePayload();
        
        // Extract user details
        currentUser.alias =
          decodeToken?.identities?.[0]?.userId ?? decodeToken["email"]!.split("@")[0];
        currentUser.ldap = decodeToken["custom:LDAP_GROUP"]?.split(",") || [];
        currentUser.firstName = decodeToken["given_name"];
        currentUser.lastName = decodeToken["family_name"];
        currentUser.email = decodeToken["email"];

        // Extract site code from location descriptor (e.g. "GYR1 - FC (Goodyear,AZ,US)" → "GYR1").
        // "" when the badge carries no site code at all — see utils/siteCode for the shapes.
        currentUser.siteCode = parseBadgeSiteCode(decodeToken["custom:AMZN_LOC_DESCR"] || "");
      }
    });
  }

  return currentUser;
};

/**
 * Get last modified timestamp for assets
 * Used to detect when a new version is deployed
 */
const getAssetsLastModified = async (): Promise<string> => {
  try {
    const res = await axios.head(`https://d1bvm0ztqnua2n.cloudfront.net`);
    const lastModified = res.headers["last-modified"];
    return typeof lastModified === "string" ? lastModified : "";
  } catch (error) {
    console.error("Error getting assets last modified:", error);
    return "";
  }
};

/**
 * Refresh the authentication token
 * Cognito SDK automatically refreshes expired tokens using the refresh token.
 * Returns the fresh ID token string, or null if refresh fails.
 * 
 * This should be called before any operation that needs a valid token
 */
const refreshToken = async (): Promise<string | null> => {
  try {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    return token;
  } catch (error) {
    console.error("Error refreshing token:", error);
    // Token refresh failed — user may need to re-authenticate
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    return null;
  }
};

/**
 * Read the current ID token without changing application authentication state.
 * Best-effort consumers such as UI logging must not sign the user out when
 * token retrieval is temporarily unavailable.
 */
export const currentIdToken = async (): Promise<string | null> => {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch {
    return null;
  }
};

/**
 * Log out the current user
 */
const logout = async (): Promise<void> => {
  try {
    await Auth.signOut();
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(SIMULATE_LDAP_GROUPS_STORAGE_KEY);
  } catch (error) {
    console.error("Error during logout:", error);
  }
};

export default {
  configureAmplify,
  login,
  logout,
  getCurrentUser,
  refreshToken,
  currentIdToken,
};
